import React from 'react'
import { motion } from 'framer-motion'
import { 
  AlertCircle,
  Brain,
  DollarSign,
  FileText,
  Globe,
  MapPin,
  Truck,
  Users
} from 'lucide-react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import Card from '../../components/Card'
import KPICard from '../../components/KPICard'
import ChartCard from '../../components/charts/ChartCard'
import BrazilMapChart from '../../components/BrazilMapChart'

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

interface AIInsight {
  category: string
  title: string
  description: string
  impact: string
  confidence: number
  recommendations: string[]
  data_support?: any
}

interface CarrierAnalysis {
  name: string
  cnpj: string
  total_documents: number
  total_freight_value: number
  average_freight_cost: number
  efficiency_score: number
  reliability_score: number
  coverage_regions: string[]
  market_share: number
  performance_trend: string
}

interface RegionalAnalysis {
  state: string
  total_documents: number
  total_freight_value: number
  average_freight_cost: number
  carrier_diversity: number
  growth_potential: string
  growth_potential_label?: string
  growth_score?: number
}

interface FreightComponent {
  component_name: string
  average_value: number
  total_value: number
  min_value: number
  max_value: number
  count: number
  percentage: number
}

interface CarrierComponentAnalysis {
  carrier_name: string
  total_freight_value: number
  components: FreightComponent[]
  total_ctes: number
}

interface ComponentComparison {
  component_name: string
  carriers: {
    carrier_name: string
    average_value: number
    count: number
  }[]
  overall_average: number
  highest_carrier: string
  lowest_carrier: string
}

interface FreightComponentsAnalysis {
  by_carrier: CarrierComponentAnalysis[]
  component_comparison: ComponentComparison[]
  total_carriers: number
  total_components: number
  component_names: string[]
}

interface AIAnalysisData {
  summary: {
    total_documents: number
    total_freight_value: number
    analysis_date: string
    period: string
  }
  carrier_analysis: CarrierAnalysis[]
  regional_analysis: RegionalAnalysis[]
  strategic_insights: AIInsight[]
  freight_components_analysis?: FreightComponentsAnalysis
  visualization_data?: {
    charts: any
    kpis: any
    summary_cards: any[]
  }
}

interface AIInsightsDisplayProps {
  data: AIAnalysisData | null
  type: 'cte' | 'nfe'
  isLoading?: boolean
  error?: string | null
  hasSearched?: boolean
  className?: string
}

// Mapeamento de códigos de estado para nomes completos
const STATE_NAMES: Record<string, string> = {
  'AC': 'Acre',
  'AL': 'Alagoas',
  'AP': 'Amapá',
  'AM': 'Amazonas',
  'BA': 'Bahia',
  'CE': 'Ceará',
  'DF': 'Distrito Federal',
  'ES': 'Espírito Santo',
  'GO': 'Goiás',
  'MA': 'Maranhão',
  'MT': 'Mato Grosso',
  'MS': 'Mato Grosso do Sul',
  'MG': 'Minas Gerais',
  'PA': 'Pará',
  'PB': 'Paraíba',
  'PR': 'Paraná',
  'PE': 'Pernambuco',
  'PI': 'Piauí',
  'RJ': 'Rio de Janeiro',
  'RN': 'Rio Grande do Norte',
  'RS': 'Rio Grande do Sul',
  'RO': 'Rondônia',
  'RR': 'Roraima',
  'SC': 'Santa Catarina',
  'SP': 'São Paulo',
  'SE': 'Sergipe',
  'TO': 'Tocantins',
  'EX': 'Exportação (Internacional)'
}

// Função para obter o nome completo do estado
const getStateName = (stateCode: string): string => {
  return STATE_NAMES[stateCode?.toUpperCase()] || stateCode
}

// Função para gerar cores dinamicamente
const generateColors = (count: number, opacity: number = 0.8) => {
  const colors: string[] = []
  for (let i = 0; i < count; i++) {
    const hue = (i * 360) / count
    colors.push(`hsla(${hue}, 70%, 60%, ${opacity})`)
  }
  return colors
}

const AIInsightsDisplay: React.FC<AIInsightsDisplayProps> = ({
  data,
  type,
  isLoading = false,
  error = null,
  hasSearched = false,
  className = ''
}) => {
  const typeConfig = {
    cte: {
      title: '🚛 Insights Inteligentes de CTEs',
      icon: Truck,
      color: 'blue',
      emptyMessage: 'Nenhum CTE encontrado para análise',
      emptyDescription: 'Não há dados suficientes para gerar insights inteligentes de CTEs.'
    },
    nfe: {
      title: '📄 Insights Inteligentes de NFes',
      icon: FileText,
      color: 'green',
      emptyMessage: 'Nenhuma NFe encontrada para análise',
      emptyDescription: 'Não há dados suficientes para gerar insights inteligentes de NFes.'
    }
  }

  const config = typeConfig[type]
  const IconComponent = config.icon

  // Loading state
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`w-full ${className}`}
      >
        <Card className="p-8" hover={false}>
          <div className="flex flex-col items-center space-y-6">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-500"></div>
              <Brain className="h-8 w-8 text-blue-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                🤖 IA Analisando Dados...
              </h3>
              <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
                Nossa inteligência artificial está processando os dados para gerar insights estratégicos
              </p>
            </div>
            <div className="flex space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </Card>
      </motion.div>
    )
  }

  // Error state
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`w-full ${className}`}
      >
        <Card className="p-6 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20" hover={false}>
          <div className="flex items-center space-x-3">
            <AlertCircle className="h-6 w-6 text-red-500" />
            <div>
              <h3 className="text-lg font-semibold text-red-700 dark:text-red-400">
                Erro na Análise de IA
              </h3>
              <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                {error}
              </p>
            </div>
          </div>
        </Card>
      </motion.div>
    )
  }

  // Empty state - sem dados ou com dados vazios
  if (hasSearched && (!data || data?.summary?.total_documents === 0)) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`w-full ${className}`}
      >
        <Card className="p-8 text-center" hover={false}>
          <div className="flex flex-col items-center space-y-4">
            <div className="p-4 rounded-full bg-gray-100 dark:bg-gray-800">
              <IconComponent className="h-12 w-12 text-gray-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                {config.emptyMessage}
              </h3>
              <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
                {config.emptyDescription}
              </p>
              <p className="text-xs mt-3 text-gray-500 dark:text-gray-400">
                Tente selecionar outro período ou verifique se há dados disponíveis
              </p>
            </div>
          </div>
        </Card>
      </motion.div>
    )
  }

  // Estado inicial
  if (!hasSearched) {
    return null
  }

  if (!data) {
    return null
  }

  // Renderizar insights de IA
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`w-full space-y-6 ${className}`}
    >
      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          label="Total de Documentos"
          value={data.summary?.total_documents?.toLocaleString() || '0'}
          icon={type === 'cte' ? Truck : FileText}
          color="blue"
        />
        <KPICard
          label="Valor Total"
          value={`R$ ${data.summary?.total_freight_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}`}
          icon={DollarSign}
          color="green"
        />
        <KPICard
          label="Transportadoras"
          value={(data.carrier_analysis?.length || 0).toString()}
          icon={Users}
          color="purple"
        />
        <KPICard
          label="Regiões Atendidas"
          value={(data.regional_analysis?.length || 0).toString()}
          icon={Globe}
          color="orange"
        />
      </div>

      {/* Insights Estratégicos - Grid Compacto */}
      <Card className="p-6" hover={false}>
        <div className="flex items-center space-x-3 mb-6">
          <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            💡 Insights Estratégicos da IA
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(data.strategic_insights || []).map((insight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="p-4 rounded-lg border-l-4 h-full flex flex-col"
              style={{ 
                borderLeftColor: insight.impact === 'Alto' ? '#ef4444' : insight.impact === 'Médio' ? '#f59e0b' : '#10b981',
                backgroundColor: 'var(--bg-secondary)',
                borderTop: '1px solid var(--border-primary)',
                borderRight: '1px solid var(--border-primary)',
                borderBottom: '1px solid var(--border-primary)'
              }}
            >
              {/* Header Compacto */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2 flex-wrap">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: insight.impact === 'Alto' ? 'rgba(239, 68, 68, 0.1)' :
                                       insight.impact === 'Médio' ? 'rgba(245, 158, 11, 0.1)' :
                                       'rgba(16, 185, 129, 0.1)',
                        color: insight.impact === 'Alto' ? '#ef4444' :
                               insight.impact === 'Médio' ? '#f59e0b' :
                               '#10b981'
                      }}
                    >
                      {insight.impact}
                    </span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        color: '#3b82f6'
                      }}
                    >
                      {Math.round(insight.confidence * 100)}%
                    </span>
                  </div>
                  <h4 className="font-semibold text-sm mb-2" style={{ color: 'var(--text-primary)' }}>
                    {insight.title}
                  </h4>
                </div>
              </div>
              
              {/* Descrição */}
              <p className="text-xs mb-3 flex-grow" style={{ color: 'var(--text-secondary)' }}>
                {insight.description}
              </p>
              
              {/* Recomendações Compactas */}
              <div className="space-y-1">
                <h5 className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                  🎯 Ações:
                </h5>
                <ul className="space-y-1">
                  {insight.recommendations.slice(0, 2).map((rec, recIndex) => (
                    <li key={recIndex} className="flex items-start space-x-1 text-xs">
                      <span style={{ color: '#10b981' }}>•</span>
                      <span style={{ color: 'var(--text-secondary)' }}>{rec}</span>
                    </li>
                  ))}
                  {insight.recommendations.length > 2 && (
                    <li className="text-xs" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                      +{insight.recommendations.length - 2} mais...
                    </li>
                  )}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Gráfico de Insights por Categoria */}
        {(data.strategic_insights || []).length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium mb-4" style={{ color: 'var(--text-primary)' }}>
              📊 Distribuição de Insights por Impacto
            </h4>
            <div className="h-64">
              <Line
                data={{
                  labels: ['Alto Impacto', 'Médio Impacto', 'Baixo Impacto'],
                  datasets: [
                    {
                      label: 'Quantidade de Insights',
                      data: [
                        (data.strategic_insights || []).filter(i => i.impact === 'Alto').length,
                        (data.strategic_insights || []).filter(i => i.impact === 'Médio').length,
                        (data.strategic_insights || []).filter(i => i.impact === 'Baixo').length,
                      ],
                      borderColor: '#163965',
                      backgroundColor: 'rgba(22, 57, 101, 0.1)',
                      borderWidth: 3,
                      fill: true,
                      tension: 0.4,
                      pointBackgroundColor: '#163965',
                      pointBorderColor: '#fff',
                      pointBorderWidth: 2,
                      pointRadius: 6,
                      pointHoverRadius: 8,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      callbacks: {
                        label: (context: any) => `${context.parsed.y} insights`,
                      },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: 1,
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        )}
      </Card>

      {/* Análise de Transportadoras */}
      {(() => {
        const topCarriers = (data.carrier_analysis || []).slice(0, 6);

        // Cores e ícones para o ranking
        const getRankingStyle = (index: number) => {
          switch (index) {
            case 0:
              return {
                gradient: 'from-yellow-400 to-amber-500',
                bgColor: 'rgba(251, 191, 36, 0.15)',
                borderColor: '#f59e0b',
                textColor: '#d97706',
                medal: '🥇',
                label: '1º Lugar'
              }
            case 1:
              return {
                gradient: 'from-gray-300 to-gray-400',
                bgColor: 'rgba(156, 163, 175, 0.15)',
                borderColor: '#9ca3af',
                textColor: '#6b7280',
                medal: '🥈',
                label: '2º Lugar'
              }
            case 2:
              return {
                gradient: 'from-amber-600 to-orange-700',
                bgColor: 'rgba(180, 83, 9, 0.15)',
                borderColor: '#b45309',
                textColor: '#92400e',
                medal: '🥉',
                label: '3º Lugar'
              }
            default:
              return {
                gradient: 'from-blue-400 to-blue-600',
                bgColor: 'rgba(59, 130, 246, 0.1)',
                borderColor: '#3b82f6',
                textColor: '#2563eb',
                medal: `#${index + 1}`,
                label: `${index + 1}º Lugar`
              }
          }
        }

        return (
          <>
            <Card className="p-6" hover={false}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                    🏆 Top Transportadoras
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Performance das principais transportadoras por volume
                  </p>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-lg">🥇</span>
                  <span className="text-lg">🥈</span>
                  <span className="text-lg">🥉</span>
                </div>
              </div>

              {/* Grid de Cards - 3 por linha em telas grandes */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {topCarriers.map((carrier, index) => {
                  const rankStyle = getRankingStyle(index)
                  const efficiencyPercent = Math.round(carrier.efficiency_score * 100)
                  const isTopThree = index < 3

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.08 }}
                      className="relative overflow-hidden rounded-xl shadow-sm"
                      style={{
                        backgroundColor: 'var(--bg-secondary)',
                        border: `1px solid ${isTopThree ? rankStyle.borderColor : 'var(--border-primary)'}`
                      }}
                    >
                      {/* Barra de cor superior com gradiente */}
                      <div className={`h-1.5 w-full bg-gradient-to-r ${rankStyle.gradient}`} />

                      {/* Badge de Ranking */}
                      <div
                        className="absolute top-4 right-4 flex items-center justify-center w-10 h-10 rounded-full shadow-md"
                        style={{
                          background: `linear-gradient(135deg, ${rankStyle.bgColor}, ${rankStyle.bgColor})`,
                          border: `2px solid ${rankStyle.borderColor}`
                        }}
                      >
                        <span className="text-lg">{rankStyle.medal}</span>
                      </div>

                      {/* Conteúdo do Card */}
                      <div className="p-4">
                        {/* Header com Nome */}
                        <div className="pr-12 mb-4">
                          <div className="flex items-center space-x-3">
                            <div
                              className="flex items-center justify-center w-11 h-11 rounded-xl shadow-sm"
                              style={{
                                background: `linear-gradient(135deg, ${rankStyle.bgColor}, transparent)`,
                                border: `1px solid ${rankStyle.borderColor}30`
                              }}
                            >
                              <Truck className="h-5 w-5" style={{ color: rankStyle.textColor }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4
                                className="font-semibold text-sm leading-tight truncate"
                                style={{ color: 'var(--text-primary)' }}
                                title={carrier.name}
                              >
                                {carrier.name}
                              </h4>
                              <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                                {carrier.total_documents} docs • {carrier.coverage_regions?.length || 0} regiões
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Volume Total - Destaque */}
                        <div
                          className="p-3 rounded-lg mb-3 text-center"
                          style={{
                            background: `linear-gradient(135deg, ${rankStyle.bgColor}, transparent)`,
                            border: `1px solid ${rankStyle.borderColor}20`
                          }}
                        >
                          <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>Volume Total</p>
                          <p className="text-lg font-bold" style={{ color: rankStyle.textColor }}>
                            R$ {(carrier.total_freight_value / 1000000).toFixed(2)}M
                          </p>
                        </div>

                        {/* Métricas em Grid */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          {/* Valor Médio */}
                          <div className="p-2 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Média/Doc</p>
                            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                              R$ {(carrier.average_freight_cost / 1000).toFixed(1)}K
                            </p>
                          </div>
                          {/* Market Share */}
                          <div className="p-2 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Market Share</p>
                            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                              {carrier.market_share ? carrier.market_share.toFixed(1) : '0.0'}%
                            </p>
                          </div>
                        </div>

                        {/* Barra de Eficiência */}
                        <div className="pt-3 border-t" style={{ borderColor: 'var(--border-primary)' }}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Eficiência</span>
                            <span
                              className="text-xs font-bold"
                              style={{
                                color: efficiencyPercent >= 70 ? '#22c55e' :
                                       efficiencyPercent >= 40 ? '#eab308' : '#ef4444'
                              }}
                            >
                              {efficiencyPercent}%
                            </span>
                          </div>
                          <div
                            className="w-full h-2 rounded-full overflow-hidden"
                            style={{ backgroundColor: 'var(--bg-primary)' }}
                          >
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${efficiencyPercent}%` }}
                              transition={{ duration: 0.8, delay: index * 0.1 }}
                              className="h-full rounded-full"
                              style={{
                                background: efficiencyPercent >= 70
                                  ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                                  : efficiencyPercent >= 40
                                    ? 'linear-gradient(90deg, #eab308, #ca8a04)'
                                    : 'linear-gradient(90deg, #ef4444, #dc2626)'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </Card>

            {/* Gráfico de Eficiência e Participação de Mercado das Transportadoras */}
            {topCarriers.length > 0 && (() => {
              const colorsEfficiency = generateColors(topCarriers.length, 0.8);
              const colorsMarketShare = generateColors(topCarriers.length, 0.5);
              const borderColors = generateColors(topCarriers.length, 1);

              return (
                <ChartCard
                  title="📊 Eficiência e Participação de Mercado das Transportadoras"
                  type="bar"
                  data={{
                    labels: topCarriers.map(c => c.name.length > 20 ? c.name.substring(0, 20) + '...' : c.name),
                    datasets: [{
                      label: 'Eficiência (%)',
                      data: topCarriers.map(c => Math.round(c.efficiency_score * 100)),
                      backgroundColor: colorsEfficiency,
                      borderColor: borderColors,
                      borderWidth: 2
                    }, {
                      label: 'Participação de Mercado (%)',
                      data: topCarriers.map(c => c.market_share ? Number(c.market_share.toFixed(1)) : 0),
                      backgroundColor: colorsMarketShare,
                      borderColor: borderColors,
                      borderWidth: 2
                    }]
                  }}
                  fullWidth={true}
                  height={250}
                />
              );
            })()}
          </>
        );
      })()}

      {/* Análise Regional */}
      <Card className="p-6" hover={false}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
              🗺️ Análise Regional
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Distribuição geográfica e potencial de crescimento
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}>Alto</span>
            <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(234, 179, 8, 0.1)', color: '#eab308' }}>Médio</span>
            <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(156, 163, 175, 0.1)', color: '#9ca3af' }}>Baixo</span>
          </div>
        </div>

        {/* Grid de Cards - 4 por linha em telas grandes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {(data.regional_analysis || []).slice(0, 12).map((region, index) => {
            const isExport = region.state === 'EX'
            const gradientColor = region.growth_potential === 'Alto'
              ? 'from-emerald-500 to-green-600'
              : region.growth_potential === 'Médio'
                ? 'from-amber-500 to-yellow-600'
                : 'from-gray-400 to-gray-500'
            const accentColor = region.growth_potential === 'Alto'
              ? '#22c55e'
              : region.growth_potential === 'Médio'
                ? '#eab308'
                : '#9ca3af'

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="relative overflow-hidden rounded-xl shadow-sm"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-primary)'
                }}
              >
                {/* Barra de cor superior */}
                <div className={`h-1.5 w-full bg-gradient-to-r ${gradientColor}`} />

                {/* Conteúdo do Card */}
                <div className="p-4">
                  {/* Header com Estado */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className="flex items-center justify-center w-12 h-12 rounded-xl shadow-sm"
                        style={{
                          background: `linear-gradient(135deg, ${accentColor}20, ${accentColor}10)`,
                          border: `1px solid ${accentColor}30`
                        }}
                      >
                        {isExport ? (
                          <Globe className="h-6 w-6" style={{ color: accentColor }} />
                        ) : (
                          <span className="text-lg font-bold" style={{ color: accentColor }}>
                            {region.state}
                          </span>
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm leading-tight" style={{ color: 'var(--text-primary)' }}>
                          {getStateName(region.state)}
                        </h4>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                          {region.total_documents} docs • {region.carrier_diversity} transp.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Métricas */}
                  <div className="space-y-3">
                    {/* Volume Total */}
                    <div className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                      <div className="flex items-center space-x-2">
                        <DollarSign className="h-4 w-4" style={{ color: 'var(--text-secondary)' }} />
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Volume Total</span>
                      </div>
                      <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                        R$ {(region.total_freight_value / 1000000).toFixed(2)}M
                      </span>
                    </div>

                    {/* Valor Médio */}
                    <div className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4" style={{ color: 'var(--text-secondary)' }} />
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Valor Médio</span>
                      </div>
                      <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                        R$ {(region.average_freight_cost / 1000).toFixed(1)}K
                      </span>
                    </div>
                  </div>

                  {/* Footer - Potencial de Crescimento */}
                  <div className="mt-4 pt-3 border-t" style={{ borderColor: 'var(--border-primary)' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Potencial</span>
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-16 h-2 rounded-full overflow-hidden"
                          style={{ backgroundColor: 'var(--bg-primary)' }}
                        >
                          <div
                            className={`h-full rounded-full bg-gradient-to-r ${gradientColor}`}
                            style={{ width: `${(region.growth_score || 0) * 100}%` }}
                          />
                        </div>
                        <span
                          className="text-xs font-semibold"
                          style={{ color: accentColor }}
                        >
                          {region.growth_potential_label || region.growth_potential}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Mapa de Distribuição Regional */}
        {(data.regional_analysis && data.regional_analysis.length > 0 && (
          <div className="mt-6">
            <BrazilMapChart data={data.regional_analysis} />
          </div>
        ))}
      </Card>

      {/* Componentes de Frete por Transportadora - NOVO */}
      {type === 'cte' && data.freight_components_analysis && data.freight_components_analysis.by_carrier && data.freight_components_analysis.by_carrier.length > 0 && (
        <Card className="p-6" hover={false}>
          <div className="flex items-center space-x-3 mb-6">
            <div>
              <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                💰 Componentes de Frete por Transportadora
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Análise detalhada dos componentes cobrados
              </p>
            </div>
          </div>

          {/* Tabela Comparativa */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              📊 Tabela Comparativa de Componentes
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ backgroundColor: '#3b82f6', borderBottom: '2px solid #3b82f6' }}>
                    <th className="text-left py-3 px-4" style={{ color: '#ffffff' }}>Transportadora</th>
                    {data.freight_components_analysis.component_names.map((compName, idx) => (
                      <th key={idx} className="text-right py-3 px-4" style={{ color: '#ffffff' }}>
                        {compName}
                      </th>
                    ))}
                    <th className="text-right py-3 px-4" style={{ color: '#ffffff' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.freight_components_analysis.by_carrier.slice(0, 5).map((carrier, idx) => (
                    <motion.tr
                      key={idx}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      style={{
                        backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.02)',
                        borderBottom: '1px solid var(--border-primary)'
                      }}
                      className="hover:bg-opacity-50"
                    >
                      <td className="py-3 px-4 font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {carrier.carrier_name}
                      </td>
                      {data.freight_components_analysis?.component_names.map((compName, compIdx) => {
                        const component = carrier.components.find(c => c.component_name === compName)
                        return (
                          <td key={compIdx} className="text-right py-3 px-4" style={{ color: 'var(--text-secondary)' }}>
                            {component && component.count > 0
                              ? `R$ ${component.average_value.toFixed(2)}`
                              : '-'
                            }
                          </td>
                        )
                      })}
                      <td className="text-right py-3 px-4 font-semibold" style={{ color: 'var(--text-primary)' }}>
                        R$ {carrier.total_freight_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Gráfico de Barras - Comparação de Componentes */}
          <div className="mb-6">
            <h4 className="text-sm font-medium mb-4" style={{ color: 'var(--text-primary)' }}>
              📈 Comparação Visual de Componentes
            </h4>
            <div className="h-96">
              <Line
                data={{
                  labels: data.freight_components_analysis.component_names,
                  datasets: data.freight_components_analysis.by_carrier.slice(0, 5).map((carrier, idx) => {
                    const colors = [
                      { border: '#163965', bg: 'rgba(22, 57, 101, 0.1)' },
                      { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
                      { border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
                      { border: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
                      { border: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' },
                    ]
                    const color = colors[idx % colors.length]
                    
                    return {
                      label: carrier.carrier_name,
                      data: data.freight_components_analysis?.component_names.map(compName => {
                        const component = carrier.components.find(c => c.component_name === compName)
                        return component && component.count > 0 ? component.average_value : 0
                      }),
                      borderColor: color.border,
                      backgroundColor: color.bg,
                      borderWidth: 2,
                      fill: false,
                      tension: 0.4,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                    }
                  })
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: 'top' as const,
                    },
                    tooltip: {
                      callbacks: {
                        label: (context: any) => {
                          return `${context.dataset.label}: R$ ${context.parsed.y.toFixed(2)}`
                        },
                      },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: (value: any) => `R$ ${value}`,
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Cards de Destaques */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.freight_components_analysis.component_comparison.slice(0, 3).map((comp, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="p-4 rounded-lg"
                style={{ 
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-primary)'
                }}
              >
                <h5 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                  {comp.component_name}
                </h5>
                <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                  Média Geral: R$ {comp.overall_average.toFixed(2)}
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Maior:</span>
                    <span className="text-xs font-medium" style={{ color: '#ef4444' }}>
                      {comp.highest_carrier}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Menor:</span>
                    <span className="text-xs font-medium" style={{ color: '#10b981' }}>
                      {comp.lowest_carrier}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Card>
      )}
    </motion.div>
  )
}

export default AIInsightsDisplay
